import { useId, type KeyboardEvent } from "react";

import { useDisabledFieldReason } from "./DisabledFieldReason";
import { Input } from "./primitives/input";
import { FieldError } from "./FieldError";

export interface UnitInputProps {
  id?: string;
  label: string;
  ariaLabel?: string;
  unit?: string;
  value: number | "";
  onChange: (value: number | "") => void;
  min?: number;
  max?: number;
  step?: number;
  suggestions?: number[];
  hint?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  clampOnBlur?: boolean;
  required?: boolean;
  name?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

const clampValue = (value: number, min: number | undefined, max: number | undefined): number => Math.max(min ?? Number.NEGATIVE_INFINITY, Math.min(max ?? Number.POSITIVE_INFINITY, value));

export function UnitInput({ id: suppliedId, label, ariaLabel, unit, value, onChange, min, max, step = 1, suggestions = [], hint, placeholder, error, disabled = false, clampOnBlur = true, required = false, name, onKeyDown }: UnitInputProps) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const disabledReason = useDisabledFieldReason();
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const describedBy = [descriptionId, error === undefined ? null : errorId, disabledReason?.id].filter((part): part is string => part !== null && part !== undefined).join(" ");
  const onBlur = (): void => {
    if (!clampOnBlur || typeof value !== "number") return;
    const clamped = clampValue(value, min, max);
    if (clamped !== value) onChange(clamped);
  };

  // Native step validation is anchored at min (min=1, step=100 rejects 1000), so the input
  // uses step="any" and the intended increment is applied to the arrow keys here.
  const onKeyDownWithStep = (event: KeyboardEvent<HTMLInputElement>): void => {
    onKeyDown?.(event);
    if (event.defaultPrevented || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
    event.preventDefault();
    const base = typeof value === "number" ? value : 0;
    onChange(clampValue(base + (event.key === "ArrowUp" ? step : -step), min, max));
  };

  return <div data-ui="" className="unit-input">
    <label className="ui-field__label" htmlFor={id}>{label}</label>
    <div className="unit-input__control">
      <Input id={id} type="number" inputMode="decimal" aria-label={ariaLabel} aria-describedby={describedBy} aria-invalid={error === undefined ? undefined : true} value={value} min={min} max={max} step="any" placeholder={placeholder} disabled={disabled} required={required} name={name} onBlur={onBlur} onKeyDown={onKeyDownWithStep} onChange={(event) => {
        const next = event.currentTarget.value;
        onChange(next.length === 0 ? "" : Number(next));
      }} className="unit-input__input" />
      {unit === undefined || unit.length === 0 ? null : <span className="unit-input__unit" aria-hidden="true">{unit}</span>}
    </div>
    {suggestions.length === 0 ? null : <div className="unit-input__suggestions" role="group" aria-label={label}>
      {suggestions.map((suggestion) => <button key={suggestion} type="button" disabled={disabled} className="unit-input__suggestion" aria-pressed={value === suggestion} onClick={() => { onChange(suggestion); }}>{suggestion}{unit === undefined ? "" : ` ${unit}`}</button>)}
    </div>}
    <span data-ui="" id={descriptionId} className="unit-input__description">{hint}</span>
    <FieldError id={errorId} className="unit-input__error" message={error} />
  </div>;
}
